import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft } from "lucide-react";
import { useLocale } from "@/i18n/useLocale";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { GoogleButton, AuthDivider } from "@/components/GoogleButton";
import { AppleButton } from "@/components/AppleButton";

const fmtErr = (detail) => {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

// Whitelist for post-auth redirects — same-origin `/partner/*` and `/partners/apply` only.
function safeReturnTo(raw) {
  if (!raw || typeof raw !== "string") return null;
  if (!raw.startsWith("/partner/") && !raw.startsWith("/partners/")) return null;
  if (raw.includes("//") || raw.includes("\\")) return null;
  return raw;
}

export default function Login() {
  useSeo({ title: "Sign in — TapPresence", description: "Sign in to your TapPresence account to manage your digital business card, leads, meetings and analytics.", path: "/login" });
  const { login } = useAuth();
  const { t } = useLocale();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isPartnerIntent = params.get("intent") === "partner";
  const partnerReturnTo = isPartnerIntent
    ? (safeReturnTo(params.get("return_to")) || "/partner/dashboard")
    : null;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (params.get("google_error")) {
      const reason = params.get("reason");
      setError(t("auth.googleError") + (reason ? ` (${reason})` : ""));
    }
    if (params.get("apple_error")) {
      setError(t("auth.appleError"));
    }
    // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      const returnTo = safeReturnTo(params.get("return_to"));
      const redirect = params.get("redirect");
      navigate(returnTo || redirect || (u?.role === "SUPER_ADMIN" ? "/control" : "/dashboard"));
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail) || err.message || t("auth.genericError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="aria-dark relative flex min-h-screen items-center justify-center overflow-y-auto bg-[#050607] px-6 py-10 pb-32 sm:pb-10" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <div className="grain-overlay" style={{ opacity: 0.05 }} />
      <div className="aria-gold-radial pointer-events-none absolute inset-0" />
      <Link to="/" className="absolute left-4 top-4 z-10 flex items-center gap-1.5 text-sm text-white/60 transition-colors hover:text-[#D6A653]" data-testid="back-to-home"><ArrowLeft className="h-4 w-4" /> {t("auth.backToHome")}</Link>
      <div className="absolute right-4 top-4 z-10"><LanguageSwitcher /></div>
      <div className="relative w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-xl shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
        <Link to="/" className="flex items-center gap-2.5" data-testid="brand-lockup">
          <img src="/tp-mark.png" alt="TapPresence" className="h-9 w-9 object-contain" />
          <span className="text-lg font-semibold tracking-tight text-white">TapPresence</span>
        </Link>
        <h1 className="mt-6 text-2xl font-medium tracking-tight text-white">{t("auth.welcomeBack")}</h1>
        <p className="mt-1 text-sm text-white/50" data-testid="login-subheading">
          {isPartnerIntent ? "Sign in to your Partner account" : t("auth.signInSub")}
        </p>
        <div className="mt-6"><GoogleButton {...(isPartnerIntent ? { intent: "partner_login", returnTo: partnerReturnTo } : {})} /></div>
        <div className="mt-3"><AppleButton {...(isPartnerIntent ? { intent: "partner_login", returnTo: partnerReturnTo } : {})} /></div>
        <AuthDivider />
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">{t("auth.email")}</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">{t("auth.password")}</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" required />
          </div>
          {error ? <p className="text-sm text-red-400" data-testid="login-error">{error}</p> : null}
          <Button type="submit" className="w-full rounded-full bg-[#D6A653] font-medium text-[#050607] transition-all hover:bg-[#E8B764] hover:shadow-[0_0_18px_rgba(214,166,83,0.35)] active:scale-[0.98]" disabled={loading} data-testid="login-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("auth.signIn")}
          </Button>
        </form>
        <p className="mt-4 text-center text-sm"><Link to="/forgot" className="text-white/50 hover:text-[#D6A653]" data-testid="login-forgot">{t("auth.forgotLink")}</Link></p>
        <p className="mt-5 text-sm text-white/50">
          {isPartnerIntent ? (
            <>
              Not a partner yet?{" "}
              <Link to="/partners/apply" className="text-[#D6A653] hover:underline" data-testid="login-signup-cta">
                Apply to become a partner
              </Link>
            </>
          ) : (
            <>
              {t("auth.newHere")}{" "}
              <Link to={`/register${window.location.search}`} className="text-[#D6A653] hover:underline" data-testid="login-signup-cta">
                {t("auth.createAccount")}
              </Link>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
